import localForage from "localforage";

import { ScriptStorage } from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import {
  EditorState,
  LocalScript,
  StoredLocalScript,
} from "pages/DevelopWorkspace/workspace.types";

const LOCAL_SCRIPTS_KEY_PREFIX = "FIREBOLT_LOCAL_SCRIPT|";

/**
 * Store is based on localForage
 * https://localforage.github.io/localForage/#settings-api-config
 *
 * TODO: add garbage collection
 * All keys are stored in localForage.keys() and we can use it to check if key is valid
 */
const localForageInstance = localForage.createInstance({
  driver: localForage.INDEXEDDB,
  name: "firebolt",
  storeName: "scripts",
  description: "Scripts storage",
});

const createLocalScriptStorage = (): ScriptStorage => {
  // TODO do garbage collection: check all scripts that don't exist in documents and remove them
  // TODO would be nice to add debounce to make all changes in one batch

  const listScripts = async (): Promise<LocalScript[]> => {
    // TODO return all scripts from storage
    // needed for garbage collection only
    return [];
  };

  const loadScript = async (id: string) => {
    const item = await localForageInstance.getItem<StoredLocalScript>(
      LOCAL_SCRIPTS_KEY_PREFIX + id
    );

    if (!item) {
      throw new Error("not found");
    }

    return item;
  };

  const updateScript = async (script: LocalScript) => {
    // Sanitize: never persist merge baseline; drop it from storage
    const sanitizedEditorState: EditorState = {
      ...script.editorState,
      original: null,
    };

    // Dual-write: persist legacy `content` alongside new `editorState`
    const storedScript: StoredLocalScript = {
      ...script,
      editorState: sanitizedEditorState,
      content: sanitizedEditorState?.doc ?? "",
    };
    return localForageInstance.setItem(
      LOCAL_SCRIPTS_KEY_PREFIX + script.id,
      storedScript
    );
  };

  const deleteScript = async (id: string): Promise<boolean> => {
    await localForageInstance.removeItem(LOCAL_SCRIPTS_KEY_PREFIX + id);

    return true;
  };

  const createScript = async (
    id: string,
    title: string,
    editorState: EditorState
  ): Promise<LocalScript> => {
    // Ensure required original field is set for new scripts; never persist merge baseline
    const normalizedEditorState: EditorState = {
      ...editorState,
      original: null,
    };
    // Dual-write: include legacy `content` derived from editorState.doc
    const scriptToStore: StoredLocalScript = {
      id,
      title,
      createdAt: new Date().toISOString(),
      editorState: normalizedEditorState,
      content: normalizedEditorState?.doc ?? "",
    };

    await localForageInstance.setItem(
      LOCAL_SCRIPTS_KEY_PREFIX + id,
      scriptToStore
    );

    // Return runtime type without the optional legacy field
    const runtimeScript: LocalScript = {
      id,
      title,
      createdAt: scriptToStore.createdAt,
      editorState: scriptToStore.editorState,
    };

    return runtimeScript;
  };

  return {
    listScripts,
    loadScript,
    updateScript,
    deleteScript,
    createScript,
  };
};

export default createLocalScriptStorage;
